/**
 * Role Management Page - Table-based layout with System Roles + Custom Roles
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Plus, Search, Shield, Layers, Eye, Edit2, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { ResponsiveTableWrapper } from '../../components/ui';
import roleService, { Role, CreateRoleRequest } from '../../services/api/roleService';
import { PermissionDefinition } from '../../components/roles/PermissionMatrix';
import apiClient from '../../services/api/apiClient';
import { CreateFromTemplateModal } from './CreateFromTemplateModal';
import { CreateSystemRoleModal } from './CreateSystemRoleModal';
import { ConfirmationDialog } from './ConfirmationDialog';
import { useAuthStore } from '../../stores/authStore';
import { useIsSuperAdmin } from '../../hooks/useIsSuperAdmin';

export const RoleManagementPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  // Org admins create org-scoped roles from a template (CreateFromTemplateModal).
  // Super admins create system-role templates from scratch (CreateSystemRoleModal).
  const [isCreateFromTemplateOpen, setIsCreateFromTemplateOpen] = useState(false);
  const [isCreateSystemRoleOpen, setIsCreateSystemRoleOpen] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    role: Role | null;
  }>({ isOpen: false, role: null });

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hasPermission } = useAuthStore();
  const isSuperAdmin = useIsSuperAdmin();

  const { data: rolesData, isLoading } = useQuery({
    queryKey: ['roles', searchQuery],
    queryFn: () => roleService.getRoles({ search: searchQuery, includeSystem: true, limit: 100 }),
  });

  // Permission catalog for the system-role create matrix — fetched lazily when the modal opens.
  const { data: allPermissions = [] } = useQuery<PermissionDefinition[]>({
    queryKey: ['all-permissions'],
    enabled: isCreateSystemRoleOpen,
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/permissions');
      const grouped = res.data as Record<string, PermissionDefinition[]>;
      return Object.values(grouped).flat();
    },
  });

  const createSystemRoleMutation = useMutation({
    mutationFn: (body: CreateRoleRequest) => roleService.createSystemRole(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setIsCreateSystemRoleOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => roleService.deleteRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setConfirmationDialog({ isOpen: false, role: null });
    },
  });

  const handleConfirmDelete = () => {
    if (confirmationDialog.role) {
      deleteMutation.mutate(confirmationDialog.role.id);
    }
  };

  const { systemRoles, customRoles } = useMemo(() => {
    if (!rolesData?.data) return { systemRoles: [], customRoles: [] };
    const sys: Role[] = [];
    const custom: Role[] = [];
    for (const role of rolesData.data) {
      if (role.isSystem && !role.organizationId) {
        sys.push(role);
      } else {
        custom.push(role);
      }
    }
    return { systemRoles: sys, customRoles: custom };
  }, [rolesData]);

  return (
    <Container>
      <Header>
        <Title>Roles</Title>
        <HeaderActions>
          <SearchContainer>
            <Input
              type="search"
              placeholder="Search roles..."
              leftIcon={<Search />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
            />
          </SearchContainer>
          {hasPermission('roles:manage') ? (
            <Button
              variant="primary-blue"
              leftIcon={<Plus />}
              onClick={() => setIsCreateSystemRoleOpen(true)}
            >
              Add System Role
            </Button>
          ) : hasPermission('role:extend') ? (
            <Button
              variant="primary-blue"
              leftIcon={<Plus />}
              onClick={() => setIsCreateFromTemplateOpen(true)}
            >
              + Create Role
            </Button>
          ) : null}
        </HeaderActions>
      </Header>

      {isLoading ? (
        <EmptyState>Loading roles...</EmptyState>
      ) : (
        <>
          {/* System Roles Table */}
          {systemRoles.length > 0 && (
            <Section>
              <SectionHeader>
                <Shield size={18} />
                <SectionTitle>System Roles</SectionTitle>
                <SectionCount>{systemRoles.length}</SectionCount>
              </SectionHeader>
              <ResponsiveTableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th>Role</Th>
                      <Th $center>Members</Th>
                      <Th $right>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {systemRoles.map((role) => (
                      <Tr key={role.id}>
                        <Td>
                          <RoleCell>
                            <RoleNameRow>
                              <RoleName>{role.name}</RoleName>
                              <Badge variant={role.isPaid ? 'warning' : 'success'}>
                                {role.isPaid ? 'Paid' : 'Free'}
                              </Badge>
                            </RoleNameRow>
                            {role.description && (
                              <RoleDescription>{role.description}</RoleDescription>
                            )}
                          </RoleCell>
                        </Td>
                        <Td $center>
                          <MemberCount>
                            {role.derivedRoleCount !== undefined ? role.derivedRoleCount : 0} derived
                          </MemberCount>
                        </Td>
                        <Td $right>
                          <ActionLink onClick={() => navigate(`/roles/${role.id}`)}>
                            {isSuperAdmin ? <Edit2 size={14} /> : <Eye size={14} />}
                            {isSuperAdmin ? 'View / Edit Permissions' : 'View Permissions'}
                          </ActionLink>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </ResponsiveTableWrapper>
            </Section>
          )}

          {/* Custom Roles Table */}
          <Section>
            <SectionHeader>
              <Layers size={18} />
              <SectionTitle>
                {customRoles.some((r) => r.isSystem) ? 'Organization Roles' : 'Custom Roles'}
              </SectionTitle>
              <SectionCount>{customRoles.length}</SectionCount>
            </SectionHeader>
            {customRoles.length === 0 ? (
              <EmptyState>
                No custom roles yet. Clone a system template or create a new role.
              </EmptyState>
            ) : (
              <ResponsiveTableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th>Role</Th>
                      <Th $center>Members</Th>
                      <Th $center>Based On</Th>
                      <Th $right>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {customRoles.map((role) => (
                      <Tr key={role.id}>
                        <Td>
                          <RoleCell>
                            <RoleNameRow>
                              <RoleName>{role.name}</RoleName>
                              {role.isSystem && <Badge variant="neutral">System</Badge>}
                            </RoleNameRow>
                            {role.description && (
                              <RoleDescription>{role.description}</RoleDescription>
                            )}
                          </RoleCell>
                        </Td>
                        <Td $center>
                          <MemberCount>
                            {(role.userCount || 0) + (role.groupCount || 0)}
                          </MemberCount>
                        </Td>
                        <Td $center>
                          {role.basedOnName ? (
                            <Badge variant="neutral">{role.basedOnName}</Badge>
                          ) : (
                            <EmptyDash>&mdash;</EmptyDash>
                          )}
                        </Td>
                        <Td $right>
                          <ActionsRow>
                            {!role.isSystem && hasPermission('role:extend') && (
                              <ActionLink onClick={() => navigate(`/roles/${role.id}`)}>
                                <Edit2 size={14} />
                                Edit
                              </ActionLink>
                            )}
                            {role.isSystem && (
                              <ActionLink onClick={() => navigate(`/roles/${role.id}`)}>
                                <Eye size={14} />
                                View
                              </ActionLink>
                            )}
                            {!role.isSystem &&
                              hasPermission('role:extend') &&
                              role.userCount === 0 &&
                              role.groupCount === 0 && (
                                <ActionLinkDanger
                                  onClick={() => setConfirmationDialog({ isOpen: true, role })}
                                >
                                  <Trash2 size={14} />
                                  Delete
                                </ActionLinkDanger>
                              )}
                          </ActionsRow>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </ResponsiveTableWrapper>
            )}
          </Section>
        </>
      )}

      {/* Org admins clone a system template into an org-scoped role. */}
      <CreateFromTemplateModal
        isOpen={isCreateFromTemplateOpen}
        onClose={() => setIsCreateFromTemplateOpen(false)}
        systemRoles={systemRoles}
      />

      {/* Super admins create system-role templates from scratch. */}
      {isCreateSystemRoleOpen && (
        <CreateSystemRoleModal
          allPermissions={allPermissions}
          isSubmitting={createSystemRoleMutation.isPending}
          onClose={() => setIsCreateSystemRoleOpen(false)}
          onSubmit={(body) => createSystemRoleMutation.mutate(body)}
        />
      )}

      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onClose={() => setConfirmationDialog({ isOpen: false, role: null })}
        onConfirm={handleConfirmDelete}
        role={confirmationDialog.role}
        isLoading={deleteMutation.isPending}
      />
    </Container>
  );
};

export default RoleManagementPage;

// ==================== Styled Components ====================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    flex-direction: column;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  max-width: 400px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
  }
`;

const Section = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.default};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};

  svg {
    color: ${({ theme }) => theme.colors.text.secondary};
    flex-shrink: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const SectionCount = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 820px;
`;

const Th = styled.th<{ $center?: boolean; $right?: boolean }>`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.light};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: ${({ $center, $right }) => ($right ? 'right' : $center ? 'center' : 'left')};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
`;

const Tr = styled.tr`
  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light}40;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray}60;
  }
`;

const Td = styled.td<{ $center?: boolean; $right?: boolean }>`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  text-align: ${({ $center, $right }) => ($right ? 'right' : $center ? 'center' : 'left')};
  vertical-align: middle;
`;

const RoleCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RoleNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const RoleName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RoleDescription = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
`;

const MemberCount = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EmptyDash = styled.span`
  color: ${({ theme }) => theme.colors.text.light};
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ActionLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary.base};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  &:hover {
    text-decoration: underline;
  }
`;

const ActionLinkDanger = styled(ActionLink)`
  color: ${({ theme }) => theme.colors.status.error.text};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;
